import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { getAdminAccess } from '@swipe4work/api-orval'
import {
  type AdminPermissionResource,
  type GetAdminAccessResult,
  type InternalDepartment,
  type InternalRoleName,
  PermissionLevel,
} from '@swipe4work/models-orval'

/**
 * Holds the current internal employee's admin access (roles, permissions, global flag) and exposes
 * permission/role checks used by the route guard and by conditional UI. Populated once after login.
 */
export const useAdminAccessStore = defineStore('admin-access', {
  state: () => ({
    access: null as GetAdminAccessResult | null,
    loaded: false as boolean,
  }),
  getters: {
    globalPermissions: (state): boolean => state.access?.globalPermissions ?? false,
  },
  actions: {
    async init() {
      if (this.loaded) {
        return
      }

      try {
        this.access = await getAdminAccess()
      } catch (e) {
        Sentry.captureException(e)
        this.access = {
          globalPermissions: false,
          permissions: [],
          roles: [],
        }
      } finally {
        this.loaded = true
      }
    },

    /** Re-fetch access (e.g. after roles change). */
    async refresh() {
      this.loaded = false
      await this.init()
    },

    hasRole(name: InternalRoleName, department: InternalDepartment): boolean {
      if (this.globalPermissions) {
        return true
      }

      return (this.access?.roles ?? []).some(r => r.name === name && r.department === department)
    },

    /** Whether the employee may access the given resource at the given level (Write implies Read; global sees all). */
    hasPermission(resource: AdminPermissionResource, level: PermissionLevel): boolean {
      if (this.globalPermissions) {
        return true
      }

      const permissions = this.access?.permissions ?? []

      return permissions.some((p) => {
        if (p.resource !== resource) {
          return false
        }

        if (p.level === level) {
          return true
        }

        // Write implies Read.
        return level === PermissionLevel.Read && p.level === PermissionLevel.Write
      })
    },
  },
})
